import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "@/store/Store";
import {PedigreeSchema} from "@/features/PedigreeEditor";

export interface StackItem {
    id: number;
    question: string;
    schema: string;
    pedigreeId?: number | null;
    pedigreeSchemaJson?: PedigreeSchema | null;
    researchSignId?: number;
    familyStepId?: string;
    familyLinks: {
        id?: number;
        number: number;
        family_link_id: number;
    }[];
    isContainer: boolean;
}

export interface AttributeRuleForNParticipantsInterface {
    formVisible: boolean,
    formVariant: "new" | "edit",
    // Текущие значения формы (для активного уровня)
    editQuestion: string,
    editSchema: string,
    editPedigreeId?: number | null,
    editPedigreeSchemaJson?: PedigreeSchema | null,
    editResearchSignId?: number,
    editFamilyStepId?: string,
    editFamilyLinks: {
        id?: number,
        number: number,
        family_link_id: number
    }[],
    editIsContainer: boolean,
    // Стек истории навигации
    editIdStack: StackItem[],
    deleteModalVisible: boolean,
    deleteId?: string,
}

function getInitialState(): AttributeRuleForNParticipantsInterface {
    return {
        formVisible: false,
        formVariant: "new",
        editQuestion: "",
        editSchema: "",
        editIsContainer: false,
        editFamilyLinks: [],
        editIdStack: [],
        deleteModalVisible: false,
    }
}

export const AttributeRuleForNParticipantsSlice = createSlice({
    name: "AttributeRuleForNParticipantsSlice",
    initialState: getInitialState(),
    reducers: {
        reset: () => {
            return getInitialState()
        },
        updateFields: (state, action: PayloadAction<Partial<AttributeRuleForNParticipantsInterface>>) => {
            Object.assign(state, action.payload)
        }
    },
});

export const {
    reset,
    updateFields
} = AttributeRuleForNParticipantsSlice.actions;

export const AttributeRuleForNParticipantsSelectors = {
    formVisible: (state: RootState) => state.attributeRuleForNParticipants.formVisible,
    formVariant: (state: RootState) => state.attributeRuleForNParticipants.formVariant,
    editQuestion: (state: RootState) => state.attributeRuleForNParticipants.editQuestion,
    editSchema: (state: RootState) => state.attributeRuleForNParticipants.editSchema,
    editPedigreeId: (state: RootState) => state.attributeRuleForNParticipants.editPedigreeId,
    editPedigreeSchemaJson: (state: RootState) => state.attributeRuleForNParticipants.editPedigreeSchemaJson,
    editResearchSignId: (state: RootState) => state.attributeRuleForNParticipants.editResearchSignId,
    editFamilyStepId: (state: RootState) => state.attributeRuleForNParticipants.editFamilyStepId,
    editFamilyLinks: (state: RootState) => state.attributeRuleForNParticipants.editFamilyLinks,
    editIsContainer: (state: RootState) => state.attributeRuleForNParticipants.editIsContainer,
    editIdStack: (state: RootState) => state.attributeRuleForNParticipants.editIdStack,
    // FamilyLinks текущего уровня (родительского контейнера)
    currentFamilyLinks: (state: RootState) => {
        const stack = state.attributeRuleForNParticipants.editIdStack;
        return stack.length > 0 ? stack[stack.length - 1].familyLinks : [];
    },
    // Текущий ID - последний элемент стека или undefined
    currentEditId: (state: RootState) => {
        const stack = state.attributeRuleForNParticipants.editIdStack;
        return stack.length > 0 ? stack[stack.length - 1].id : undefined;
    },
    // ID родительского контейнера (для создания новых внутри)
    // Это предпоследний элемент стека — родитель текущего редактируемого
    parentContainerId: (state: RootState) => {
        const stack = state.attributeRuleForNParticipants.editIdStack;
        return stack.length >= 2 ? stack[stack.length - 2].id : undefined;
    },
    // Можно ли вернуться назад
    canGoBack: (state: RootState) => state.attributeRuleForNParticipants.editIdStack.length > 1,
    deleteModalVisible: (state: RootState) => state.attributeRuleForNParticipants.deleteModalVisible,
    deleteId: (state: RootState) => state.attributeRuleForNParticipants.deleteId,
}

export const AttributeRuleForNParticipantsReducer = AttributeRuleForNParticipantsSlice.reducer;
